'use client';

import { useCallback } from 'react';

import { type IDramaInfo } from '@/apis/search';
import Image from '@/components/image';
import { TIME } from '@/constants';
import { useDebounce, useInView, useOnceWhen } from '@/hooks';
import { ETrackerEvents, trackCustom } from '@/utils/tracker';

import HighlightFragment from './highlight-fragment';

interface ISearchCardItemProps {
    item: IDramaInfo;
    position: number;
    keyword: string;
    searchId: string;
    onClick: (item: IDramaInfo) => void;
}

export default function SearchCardItem(props: ISearchCardItemProps) {
    const { item, onClick, position, keyword, searchId } = props;
    const {
        cover_url,
        title,
        description,
        category_list = [],
        highlight_title_fragment,
        highlight_description_fragment,
        drama_id,
    } = item;

    const { ref, inView } = useInView<HTMLDivElement>();
    useOnceWhen(inView, () => {
        trackCustom(ETrackerEvents.BOOK_SHOW, {
            book_id: drama_id,
            module_name: 'search_result',
            position,
            query: keyword,
            search_id: searchId,
        });
    });
    const handleCardClick = useCallback(() => {
        onClick(item);

        trackCustom(ETrackerEvents.BOOK_CLICK, {
            book_id: drama_id,
            module_name: 'search_result',
            position,
            query: keyword,
            search_id: searchId,
        });
    }, [drama_id, item, keyword, onClick, position, searchId]);

    const handleClick = useDebounce(handleCardClick, TIME.NAVIGATE_DEBOUNCE, {
        leading: true,
        trailing: false,
    });

    return (
        <article ref={ref} className='flex items-center gap-[12px]' onClick={handleClick}>
            <div className='relative h-[107px] w-[80px] shrink-0 overflow-hidden rounded-[8px] bg-[#E5E7EB]'>
                <Image src={cover_url} />
            </div>
            <div className='flex min-w-0 flex-1 flex-col gap-[4px]'>
                <div className='line-clamp-1 font-poppins-medium text-[14px] leading-[20px] text-black'>
                    <HighlightFragment fragment={highlight_title_fragment} fallback={title} />
                </div>
                <div className='flex flex-col gap-[12px]'>
                    <div className='line-clamp-2 text-[12px] leading-[16px] text-[#888]'>
                        <HighlightFragment fragment={highlight_description_fragment} fallback={description} />
                    </div>
                    <div className='flex flex-wrap items-center gap-[4px]'>
                        {category_list.slice(0, 3).map(({ name }) => (
                            <span
                                key={name}
                                className='rounded-[4px] bg-[#ECE7F7] px-[6px] py-[2px] text-[12px] leading-[16px] text-[#816AAE]'
                            >
                                {name}
                            </span>
                        ))}
                    </div>
                </div>
            </div>
        </article>
    );
}
